export default function PyVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - Python Runner &amp; Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for Python Runner &amp;
            Editor.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I write and run a Python script?
              </h3>
              <p className="mt-1">
                Tap the + button to create a new script. Write your Python code
                in the editor, then tap the Run button (play icon) to execute
                it. Output appears in the console panel below the editor. Python
                runs entirely on your device — no internet connection is
                required to execute scripts.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I install a Python package?
              </h3>
              <p className="mt-1">
                Open the Package Manager from the sidebar or menu. Search for
                the package you need and tap Install. The app will download and
                install it locally on your device. An internet connection is
                required for installation. Once installed, packages are
                available to all your scripts.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I plot charts with matplotlib?
              </h3>
              <p className="mt-1">
                Import matplotlib in your script as usual (e.g.,{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  import matplotlib.pyplot as plt
                </code>
                ). Call{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  plt.show()
                </code>{" "}
                at the end of your plotting code. The chart will appear inline
                in the output panel.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I use Notebook Mode?
              </h3>
              <p className="mt-1">
                Create a new notebook file (.ipynb) or switch to Notebook Mode
                from the editor menu. Each cell can be run independently by
                tapping the Run button on that cell. Outputs, including charts,
                appear directly below each cell.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I manage my projects?
              </h3>
              <p className="mt-1">
                Open the Project Manager from the home screen or sidebar. You
                can create new projects, rename them, move files between
                projects, and delete projects you no longer need. All project
                data is stored locally on your device.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I use the Examples Library?
              </h3>
              <p className="mt-1">
                Tap the Examples button on the home screen or from the editor
                menu. Browse built-in example scripts covering common Python
                topics, data science, plotting, and more. Tap any example to
                open it in the editor and run or modify it.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Which Python version does the app use?
              </h3>
              <p className="mt-1">
                PyVEdit uses Chaquopy, an embedded Python runtime for Android.
                The supported Python version depends on your app version — check
                Settings &gt; About for the exact Python version in use.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why is a package not available to install?
              </h3>
              <p className="mt-1">
                Not all PyPI packages are supported on Android. Packages that
                depend on native C/C++ extensions must be pre-compiled for
                Android by Chaquopy. If a package you need is not available,
                email us and we will investigate adding support in a future
                update.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all ads
                with a one-time in-app purchase from Settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have the latest
                version from Google Play. If a specific script causes the crash,
                check for infinite loops or very large memory usage. Email us
                with your device model, OS version, app version, and steps to
                reproduce.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/pyvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/pyvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/pyvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/pyvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> Python Runner &amp; Editor
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.pyvedit
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
